import { useState, useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useEquipment } from '../../../contexts/EquipmentContext';
import { useAuthUser } from '../../../contexts/AuthUserContext';
import CategoryDetails from '../../../components/inventory/CategoryDetails';
import ChildDetails from '../../../components/inventory/ChildDetails';
import EditEquipment from '../../../components/inventory/EditEquipment';
import NewEquipment from '../../../components/inventory/NewEquipment';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { searchInventory } from '../../../utils/searchUtils';

// Helper function to format changedBy for display
const formatChangedBy = (changedBy, currentUser) => {
    if (!changedBy) return 'Unknown';
    
    // If it's an auth0 ID, try to match with current user
    if (changedBy.startsWith('auth0|')) {
        // Check if it matches current user's ID
        if (currentUser?._raw?.sub === changedBy) {
            // Use current user's name or email
            return currentUser.name || currentUser.email || changedBy;
        }
        // Otherwise, just return a cleaner format
        return changedBy.replace('auth0|', 'User ');
    }
    
    // If it's already an email or name, return as-is
    return changedBy;
};

const Inventory = () => {
    const {
        equipmentData,
        pendingCreations,
        pendingChanges,
        loading,
        error,
        isWardsCustomer,
        refreshData,
        fetchPendingChangesData
    } = useEquipment();
    
    const currentUser = useAuthUser();
    
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [copiedId, setCopiedId] = useState(null);
    const [selectedChild, setSelectedChild] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isNewEquipmentMode, setIsNewEquipmentMode] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    // Memoize merged equipment data to prevent infinite loops
    const mergedEquipmentData = useMemo(() => {
        if (!equipmentData) {
            return equipmentData;
        }

        // Create a deep copy of equipmentData to avoid modifying the original
        const mergedData = {
            ...equipmentData,
            categories: equipmentData.categories ? equipmentData.categories.map(cat => ({
                ...cat,
                childItems: cat.childItems ? cat.childItems.map(child => ({ ...child })) : []
            })) : []
        };
        
        // Process pending changes (both modifications and creations)
        if (pendingChanges && Array.isArray(pendingChanges) && pendingChanges.length > 0) {
            // Filter for equipment changes only (exclude policy changes)
            // If changeStatus exists, it should be 'pending', otherwise include all
            const equipmentPendingChanges = pendingChanges.filter(change => 
                (change.changeType === 'parent' || change.changeType === 'child') &&
                (!change.changeStatus || change.changeStatus === 'pending')
            );

            // Separate modifications and creations
            // A change is a modification if it has modifiedData and is not a creation
            // A change is a creation if it has newData or changeAction is 'create'
            const pendingModifications = equipmentPendingChanges.filter(change => {
                const hasModifiedData = change.modifiedData && Object.keys(change.modifiedData).length > 0;
                const isUpdate = change.changeAction === 'update' || change.changeAction === 'modify';
                const isNotCreation = change.changeAction !== 'create' && !change.newData;
                return (hasModifiedData || isUpdate) && isNotCreation;
            });
            
            const pendingCreationsList = equipmentPendingChanges.filter(change => {
                const hasNewData = change.newData && Object.keys(change.newData).length > 0;
                const isCreate = change.changeAction === 'create';
                return hasNewData || isCreate;
            });

            // Debug logging - show full structure
            // console.log('All pending changes:', JSON.stringify(pendingChanges, null, 2));
            // console.log('Equipment pending changes:', JSON.stringify(equipmentPendingChanges, null, 2));
            
            // Log each change's structure
            // equipmentPendingChanges.forEach((change, idx) => {
            //     console.log(`Change ${idx}:`, {
            //         changeType: change.changeType,
            //         changeAction: change.changeAction,
            //         changeStatus: change.changeStatus,
            //         parentEqpId: change.parentEqpId,
            //         childCategory: change.childCategory,
            //         hasModifiedData: !!change.modifiedData,
            //         hasNewData: !!change.newData,
            //         keys: Object.keys(change)
            //     });
            // });
            
            // console.log('Pending modifications:', pendingModifications);
            // console.log('Pending creations:', pendingCreationsList);

            // Apply modifications to existing categories and children
            pendingModifications.forEach(change => {
                if (change.changeType === 'parent') {
                    // Find and update the parent category
                    const categoryIndex = mergedData.categories.findIndex(cat => 
                        String(cat.parentEqpId) === String(change.parentEqpId)
                    );
                    
                    if (categoryIndex !== -1 && change.modifiedData) {
                        mergedData.categories[categoryIndex] = {
                            ...mergedData.categories[categoryIndex],
                            ...change.modifiedData,
                            _hasPendingChanges: true,
                            _pendingChangeId: change.id,
                            _changedBy: formatChangedBy(change.changedBy || change.changed_by, currentUser),
                            _changedAt: change.changedAt || change.changed_at || new Date().toISOString()
                        };
                    }
                } else if (change.changeType === 'child') {
                    // Find the parent category and update the child
                    const parentCategory = mergedData.categories.find(cat => 
                        String(cat.parentEqpId) === String(change.parentEqpId)
                    );
                    
                    if (parentCategory && parentCategory.childItems && change.modifiedData) {
                        const childIndex = parentCategory.childItems.findIndex(child => 
                            String(child.childCategory) === String(change.childCategory)
                        );
                        
                        if (childIndex !== -1) {
                            parentCategory.childItems[childIndex] = {
                                ...parentCategory.childItems[childIndex],
                                ...change.modifiedData,
                                _hasPendingChanges: true,
                                _pendingChangeId: change.id,
                                _changedBy: formatChangedBy(change.changedBy || change.changed_by, currentUser),
                                _changedAt: change.changedAt || change.changed_at || new Date().toISOString()
                            };
                        }
                    }
                }
            });

            // Add pending creations
            const pendingParentCreations = pendingCreationsList.filter(creation => 
                creation.changeType === 'parent'
            );
            const pendingChildCreations = pendingCreationsList.filter(creation => 
                creation.changeType === 'child'
            );

            // Add virtual parent categories for pending parent creations
            if (pendingParentCreations.length > 0) {
                const virtualCategories = pendingParentCreations.map(creation => ({
                    parentEqpId: `PENDING_${creation.id}`,
                    parentCategory: creation.newData?.parentCategory || 'New Category',
                    typicalUses: creation.newData?.typicalUses || '',
                    typicalFlow: creation.newData?.typicalFlow || '',
                    defaults: creation.newData?.defaults || '',
                    qualifyingQuestions: creation.newData?.qualifyingQuestions || '',
                    specialNotes: creation.newData?.specialNotes || '',
                    pricingConsiderations: creation.newData?.pricingConsiderations || '',
                    childItems: [],
                                _isPendingCreation: true,
                                _creationId: creation.id,
                                _changedBy: formatChangedBy(creation.changedBy || creation.changed_by, currentUser),
                                _changedAt: creation.changedAt || creation.changed_at
                }));
                
                // Add virtual categories to the merged data
                mergedData.categories = [...mergedData.categories, ...virtualCategories];
            }

            // Add virtual child items for pending child creations
            if (pendingChildCreations.length > 0) {
                pendingChildCreations.forEach(creation => {
                    const parentEqpId = creation.parentEqpId;
                    const parentCategory = mergedData.categories.find(cat => 
                        cat.parentEqpId === parentEqpId || 
                        String(cat.parentEqpId) === String(parentEqpId)
                    );
                    
                    if (parentCategory) {
                        if (!parentCategory.childItems) {
                            parentCategory.childItems = [];
                        }
                        const virtualChild = {
                            childEqpId: `PENDING_${creation.id}`,
                            childCategory: creation.newData?.childCategory || 'New Item',
                            model: creation.newData?.model || '',
                            brand: creation.newData?.brand || '',
                            customerReference: creation.newData?.customerReference || '',
                            commonSynonyms: creation.newData?.commonSynonyms || '',
                            keySellingPoints: creation.newData?.keySellingPoints || '',
                            description: creation.newData?.description || '',
                            pricing: creation.newData?.pricing || '',
                                _isPendingCreation: true,
                                _creationId: creation.id,
                                _changedBy: formatChangedBy(creation.changedBy || creation.changed_by, currentUser),
                                _changedAt: creation.changedAt || creation.changed_at
                        };

                        parentCategory.childItems.push(virtualChild);
                    }
                });
            }
        }

        return mergedData;
    }, [equipmentData, pendingChanges]);

    // Filter categories based on search query
    const filteredCategories = useMemo(() => {
        if (!mergedEquipmentData || !mergedEquipmentData.categories) {
            return [];
        }
        
        if (!searchQuery || searchQuery.trim() === '') {
            return mergedEquipmentData.categories;
        }
        
        const filtered = searchInventory(mergedEquipmentData, searchQuery);
        return filtered;
    }, [mergedEquipmentData, searchQuery]);

    // Extract URL params for stable dependency tracking
    const categoryId = searchParams.get('categoryId');
    const childId = searchParams.get('childId');

    // Sync URL with component state on mount and URL changes
    useEffect(() => {
        if (!mergedEquipmentData) return;
        
        // Reset state if no category in URL
        if (!categoryId) {
            setSelectedCategory(null);
            setSelectedChild(null);
            return;
        }
        
        const category = mergedEquipmentData.categories.find(cat => cat.parentEqpId === categoryId);
        if (category) {
            setSelectedCategory(category);
            
            if (childId) {
                const child = category.childItems?.find(item => item.childCategory === childId);
                if (child) {
                    setSelectedChild(child);
                } else {
                    setSelectedChild(null);
                }
            } else {
                setSelectedChild(null);
            }
        } else {
            // Category not found, reset state
            setSelectedCategory(null);
            setSelectedChild(null);
        }
    }, [categoryId, childId, mergedEquipmentData]);

    const toTitleCase = (str) => {
        return str.replace(
            /\w\S*/g,
            (text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase()
        );
    }

    // Render loading state
    if (loading) {
        return (
            <div className="responsive-page-layout scrollable">
                <div className="flex flex-row justify-between items-center w-full z-10 transition-all duration-700 ease-out">
                    <div className="flex flex-row gap-2 items-start text-2xl">
                        <Icon icon="lsicon:inventory-filled" className="text-primary text-3xl"></Icon>
                        Inventory
                    </div>
                </div>
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <Icon icon="mdi:loading" className="text-4xl text-primary animate-spin mx-auto mb-4" />
                        <p className="text-gray-600">Loading equipment inventory...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Render error state
    if (error) {
        return (
            <div className="responsive-page-layout scrollable">
                <div className="flex flex-row justify-between items-center w-full z-10 transition-all duration-700 ease-out">
                    <div className="flex flex-row gap-2 items-start text-2xl">
                        <Icon icon="lsicon:inventory-filled" className="text-primary text-3xl"></Icon>
                        Inventory
                    </div>
                </div>
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <Icon icon="mdi:alert-circle" className="text-4xl text-error mx-auto mb-4" />
                        <p className="text-error mb-2">Error loading equipment inventory</p>
                        <p className="text-gray-600 text-sm">{error.message}</p>
                    </div>
                </div>
            </div>
        );
    }

    // Render access denied state
    if (!isWardsCustomer()) {
        return (
            <div className="responsive-page-layout scrollable">
                <div className="flex flex-row justify-between items-center w-full z-10 transition-all duration-700 ease-out">
                    <div className="flex flex-row gap-2 items-start text-2xl">
                        <Icon icon="lsicon:inventory-filled" className="text-primary text-3xl"></Icon>
                        Inventory
                    </div>
                </div>
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <Icon icon="mdi:lock" className="text-4xl text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600 mb-2">Access Restricted</p>
                        <p className="text-gray-500 text-sm">Equipment inventory is only available for Wards customers</p>
                    </div>
                </div>
            </div>
        );
    }

    const handleCopy = (categoryId) => {
        navigator.clipboard.writeText(categoryId);
        setCopiedId(categoryId);
        setTimeout(() => setCopiedId(null), 1000);
    }

    const handleCategorySelect = (category) => {
        setSelectedCategory(category);
        setSelectedChild(null);
        setSearchParams({
            categoryId: category.parentEqpId
        });
    };

    const handleCategoryClose = () => {
        setSelectedCategory(null);
        setSelectedChild(null);
        setSearchParams({});
    };

    const handleChildSelect = (child) => {
        setSelectedChild(child);
        setSearchParams({
            categoryId: selectedCategory.parentEqpId,
            childId: child.childCategory
        });
    };

    const handleChildClose = () => {
        setSelectedChild(null);
        setSearchParams({
            categoryId: selectedCategory.parentEqpId
        });
    };

    const handleEditEquipment = () => {
        setIsEditMode(true);
    };

    const handleBackToInventory = () => {
        setIsEditMode(false);
    };

    const handleBackFromNewEquipment = () => {
        setIsNewEquipmentMode(false);
    };

    const handleEditCategory = (category) => {
        setSelectedCategory(category);
        setSelectedChild(null);
        setIsEditMode(true);
    };

    const handleEditChild = (child) => {
        // Find the parent category for this child
        const parentCategory = mergedEquipmentData?.categories?.find(cat => 
            cat.childItems?.some(childItem => childItem.childCategory === child.childCategory)
        );
        if (parentCategory) {
            setSelectedCategory(parentCategory);
            setSelectedChild(child);
            setIsEditMode(true);
        }
    };

    const handleAddEquipment = () => {
        setIsNewEquipmentMode(true);
    };

    // Render equipment data
    if (isEditMode) {
        return (
            <EditEquipment 
                onBack={handleBackToInventory}
                initialParentId={selectedCategory?.parentEqpId}
                initialChildId={selectedChild?.childCategory}
            />
        );
    }

    if (isNewEquipmentMode) {
        return (
            <NewEquipment 
                onBack={handleBackFromNewEquipment}
                initialParentId={selectedCategory?.parentEqpId}
            />
        );
    }

    return (
        <div className="responsive-page-layout scrollable">
            <div className="flex flex-col md:flex-row gap-2 md:gap-0 justify-between items-start md:items-center w-full z-10 transition-all duration-700 ease-out">
                <div className="header-textbox">
                    <Icon icon="lsicon:inventory-filled" className="header-icon"></Icon>
                    Inventory
                </div>
                <div className="flex flex-row gap-2">
                    <button 
                        onClick={handleEditEquipment}
                        className="flex flex-row gap-2 items-center text-font-primary button-container px-3 py-2 rounded-lg text-sm group hover:bg-primary transition-colors duration-300"
                    >
                        <Icon icon="fluent:box-edit-20-regular" className="text-xl text-primary group-hover:text-background" />
                        <span className="text-sm font-medium group-hover:text-background">Edit Equipment</span>
                    </button>
                    <button 
                        onClick={handleAddEquipment}
                        className="flex flex-row gap-2 items-center text-font-primary button-container px-3 py-2 rounded-lg text-sm group hover:bg-primary transition-colors duration-300"
                    >
                        <Icon icon="proicons:box-add" className="text-xl text-primary group-hover:text-background" />
                        <span className="text-sm font-medium group-hover:text-background">Add Equipment</span>
                    </button>
                </div>
            </div>

            {/* Summary or Breadcrumbs */}
            {mergedEquipmentData && (
                <>
                    {!selectedCategory ? (
                        // Equipment Summary (default state) - hidden on md and below
                        <div className="inventory-summary-responsive grid grid-cols-1 md:grid-cols-3 gap-3 w-full">
                            <div className="card-container px-3 py-2 rounded-lg flex flex-row gap-2 items-center justify-between">
                                <div className="text-font-primary flex flex-row items-center gap-2">
                                    <Icon icon="ic:round-category" className="text-2xl text-blue-600" />
                                    Total Categories
                                </div>
                                <div className="text-lg font-bold text-blue-600">
                                    {mergedEquipmentData.totalCategories || 0}
                                </div>
                            </div>
                            <div className="card-container px-3 py-2 rounded-lg flex flex-row gap-2 items-center justify-between">
                                <div className="text-font-primary flex flex-row items-center gap-2">
                                    <Icon icon="ix:box-open" className="text-2xl text-accent" />
                                    Total Items
                                </div>
                                <div className="text-lg font-bold text-green-600">
                                    {mergedEquipmentData.categories?.reduce((total, cat) => 
                                        total + (cat.childItems?.length || 0), 0) || 0}
                                </div>
                            </div>
                            <div className="card-container px-3 py-2 rounded-lg flex flex-row gap-2 items-center justify-between">
                                <div className="text-font-primary flex flex-row items-center gap-2">
                                    <Icon icon="material-symbols:rate-review-rounded" className="text-2xl text-purple-600" />
                                    Review Needed
                                </div>
                                <div className="text-lg font-bold text-purple-600">
                                    {mergedEquipmentData.categories?.length || 0}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-row items-center gap-1 text-font-primary overflow-hidden w-full">
                            {/* Back button - goes to parent level, not browser history */}
                            {selectedChild && (
                                <button
                                    onClick={handleChildClose}
                                    className="flex-shrink-0 flex items-center justify-center rounded-lg hover:bg-background transition-colors cursor-pointer"
                                    title="Go back to parent"
                                >
                                    <Icon icon="mdi:chevron-left" className="text-3xl text-accent hover:text-primary transition-colors" />
                                </button>
                            )}
                            {!selectedChild && selectedCategory && (
                                <button
                                    onClick={handleCategoryClose}
                                    className="flex-shrink-0 flex items-center justify-center rounded-lg hover:bg-background transition-colors cursor-pointer"
                                    title="Go back to categories"
                                >
                                    <Icon icon="mdi:chevron-left" className="text-3xl text-accent hover:text-primary transition-colors" />
                                </button>
                            )}
                            <span 
                                className="flex-shrink-0 flex items-center justify-center gap-1 group transition-colors cursor-pointer"
                                onClick={handleCategoryClose}
                            >
                                <Icon icon="bx:category" className="text-xl text-font-secondary group-hover:text-primary transition-colors" />
                                <span className="text-font-secondary group-hover:text-font-primary transition-colors">Categories</span>
                            </span>
                            <Icon icon="mdi:chevron-right" className="flex-shrink-0 text-lg text-font-secondary" />
                            <span 
                                className={`flex items-center justify-center gap-1 group transition-colors cursor-pointer min-w-0 ${selectedChild ? 'text-font-secondary text-base cursor-pointer hover:text-primary transition-colors' : 'text-font-primary font-semibold'}`}
                                onClick={selectedChild ? () => handleChildClose() : undefined}
                            >
                                <Icon icon="duo-icons:box-2" className={`flex-shrink-0 text-2xl ${selectedChild ? 'text-font-secondary group-hover:text-primary transition-colors' : 'text-primary'}`} />
                                <span className={`truncate ${selectedChild ? 'text-font-secondary group-hover:text-font-primary transition-colors' : 'text-font-primary'}`}>
                                    {selectedCategory.parentCategory || 'Unnamed Category'}
                                </span>
                            </span>
                            {selectedChild && (
                                <>
                                    <Icon icon="mdi:chevron-right" className="flex-shrink-0 text-lg text-font-secondary" />
                                    <span className="flex items-center justify-center gap-1 min-w-0 overflow-hidden">
                                        <Icon icon="mdi:package-variant" className="flex-shrink-0 text-2xl text-primary" />
                                        <span className="text-font-primary text-base font-semibold truncate">
                                            {selectedChild.childCategory || 'Unnamed Item'}
                                        </span>
                                    </span>
                                </>
                            )}
                        </div>
                    )}
                </>
            )}

            {/* Categories */}
            {mergedEquipmentData?.categories && mergedEquipmentData.categories.length > 0 ? (
                <div className="flex flex-1 gap-2 h-full w-full min-h-0">
                    {/* Categories List */}
                    <div className={`flex flex-col w-full transition-all duration-300 ease-out overflow-hidden
                        ${selectedCategory && !selectedChild ? 'hidden 2xl:flex 2xl:w-2/5' : selectedChild ? 'hidden' : 'w-full'}
                    `}>
                        <div className={`w-full h-full flex flex-col gap-3 ${selectedCategory ? '' : ''}`}>
                            {/* Header */}
                            <div className={`inventory-header-responsive flex-shrink-0 flex flex-col md:flex-row gap-2 md:gap-0 items-start md:items-center justify-between text-secondary text-sm ${selectedCategory ? 'px-3 pb-2' : ''}`}>
                                <div className="subheader-textbox">
                                    {/* <Icon icon="bx:category" className="responsive-icon-sm text-primary" /> */}
                                    <span className="text-font-primary">Categories</span>
                                </div>
                                <div className="inventory-controls-responsive flex flex-row gap-2">
                                    <button className="flex flex-row gap-2 items-center text-font-primary button-container p-2 rounded-lg text-sm">
                                        <Icon icon="material-symbols:rate-review-rounded" className="text-xl text-primary" />
                                        <span className="hidden lg:inline">Review</span>
                                    </button>
                                    <div className="relative flex items-center">
                                        <Icon icon="material-symbols:search" className="absolute left-3 text-xl text-font-secondary pointer-events-none" />
                                        <input 
                                            type="text" 
                                            placeholder="Search Inventory" 
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="inventory-search-responsive flex flex-row gap-2 items-center text-font-primary input-textbox pl-10 pr-3 py-2 rounded-lg text-sm active:outline-none focus:outline-none" 
                                        />
                                        {searchQuery && (
                                            <button
                                                onClick={() => setSearchQuery('')}
                                                className="absolute right-3 text-font-secondary hover:text-font-primary transition-colors"
                                            >
                                                <Icon icon="material-symbols:close" className="text-xl" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className={`flex-1 overflow-y-auto scrollable rounded-lg ${selectedCategory ? '' : 'card-container p-0 bg-background'}`}>
                                {filteredCategories.length === 0 && searchQuery ? (
                                    <div className="flex items-center justify-center h-64">
                                        <div className="text-center">
                                            <Icon icon="material-symbols:search-off" className="text-6xl text-bgAccent mb-4" />
                                            <p className="text-font-secondary text-lg">No inventory found for "{searchQuery}"</p>
                                        </div>
                                    </div>
                                ) : (
                                    filteredCategories.map((category, index) => (
                                    <div 
                                        key={category.parentEqpId || `category_${index}`}
                                        className={`w-full flex flex-row justify-between items-center px-4 py-3 border-b border-bgAccent interactive-card-container cursor-pointer transition-all ease-out-in duration-300
                                            ${selectedCategory?.parentEqpId === category.parentEqpId ? 'tag-secondary' : ''}`}
                                        onClick={() => handleCategorySelect(category)}
                                    >
                                        <div className="w-full flex flex-col gap-2">
                                            {/* Category Header */}
                                            <div className="flex items-center gap-2">
                                                {/* <Icon icon="duo-icons:box-2" className={`text-4xl ${
                                                    category._hasPendingChanges ? 'text-orange-500' :
                                                    category._isPendingCreation ? 'text-primary opacity-100' :
                                                    'text-accent opacity-30'
                                                }`} /> */}
                                                <div className="flex-1">
                                                    <h3 className="text-lg font-semibold text-font-primary capitalize">
                                                        {category.parentCategory || 'Unnamed Category'}
                                                    </h3>
                                                    <div className="text-sm text-font-secondary">
                                                        {category.childItems?.length || 0} items
                                                        {category._hasPendingChanges && (
                                                            <span className="text-orange-600"> • Modified by {category._changedBy}</span>
                                                        )}
                                                        {category._isPendingCreation && (
                                                            <span className="text-blue-600"> • Created by {category._changedBy}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="typical-uses-responsive flex flex-row items-center gap-2 w-full">
                                                <p className="text-sm text-font-primary">Typical Uses:</p>
                                                <div className="flex flex-wrap gap-1">
                                                    {category.typicalUses?.split(',').slice(0, 3).map((use, useIndex) => (
                                                        <span key={`${category.parentEqpId}_use_${useIndex}`} className={`text-xs text-font-primary p-1.5 rounded-md ${selectedCategory?.parentEqpId === category.parentEqpId ? 'tag-secondary' : 'tag-gray'}`}>
                                                            {toTitleCase(use.trim())}
                                                        </span>
                                                    ))}
                                                    {category.typicalUses?.split(',').length > 3 && (
                                                        <span className="text-sm text-font-secondary px-2 mt-2 rounded-md">
                                                            <span className="text-primary font-bold">+{category.typicalUses.split(',').length - 3}</span> more
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <Icon icon="mdi:chevron-right" className="text-3xl text-primary" />
                                    </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Category Details Panel - Hidden on screens < 1240px when child is selected */}
                    {selectedCategory && !selectedChild && (
                        <CategoryDetails
                            selectedCategory={selectedCategory}
                            selectedChild={selectedChild}
                            copiedId={copiedId}
                            onCopy={handleCopy}
                            onChildSelect={handleChildSelect}
                            onEditCategory={handleEditCategory}
                        />
                    )}
                    
                    {/* Category Details Panel when child is selected - only shows on xl+ screens */}
                    {selectedCategory && selectedChild && (
                        <div className="hidden 2xl:block">
                            <CategoryDetails
                                selectedCategory={selectedCategory}
                                selectedChild={selectedChild}
                                copiedId={copiedId}
                                onCopy={handleCopy}
                                onChildSelect={handleChildSelect}
                                onEditCategory={handleEditCategory}
                            />
                        </div>
                    )}

                    {/* Child Details Panel - Takes priority on screens < 1240px */}
                    {selectedChild && (
                        <ChildDetails
                            selectedChild={selectedChild}
                            copiedId={copiedId}
                            onCopy={handleCopy}
                            onEditChild={handleEditChild}
                        />
                    )}
                </div>
            ) : (
                <div className="bg-bgSecondary card-container p-8 text-center">
                    <Icon icon="mdi:package-variant-closed" className="text-4xl text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-font-primary mb-2">No Equipment Data</h3>
                    <p className="text-font-secondary mb-4">No equipment categories found in the inventory</p>
                </div>
            )}
        </div>
    );
};

export default Inventory;